/**
 * Chat widget view for the booking form shown by chatbot
 */
import springyView from '../../tools/SpringyView.js';

const bformView = Object.create(springyView);

bformView.submitted = false;
// is it in a preview widget/bot
bformView.preview = false;

bformView.events = {
    keydown: 'trapFocus'
};

/**
 * Wrapper for springyView.init in case we need extra functionality.
 * @param {ChatWidget} model Widget object for this view.
 * @param {PatronSession} patronSession Current patron info
 * @param {SuiEventEmitter} emitter widget emitter
 * @param {Boolean} preview For bot preview
 */
bformView.initialize = function({
    model,
    patronSession,
    emitter,
    preview = false
}) {
    this.patronSession = patronSession;
    this.emitter = emitter;
    this.init({
        model,
        el: document.getElementById('bform')
    });
    this.preview = preview;
};
/**
 * Show this view and set focus as appopriate
 */
bformView.showView = function() {
    this.el.classList.remove('hidden');
    const firstEl = this.el.querySelector('input, textarea, select, a, button, [tabindex]');
    if (firstEl) {
        firstEl.focus();
    }
};
/**
 * When a required checkbox is changed we have to adjust the required attribute
 * on all of them else the browser will not validate
 * @param {Event} ev Checkbox input change.
 */
bformView.handleRequiredCheckBoxes = function(ev) {
    const fieldName = ev.target.name;
    const isChecked = (ev.target.checked === true);
    // if checked remove required from related inputs.
    if (isChecked) {
        const relatedChecks = this.el.querySelectorAll(`input[type=checkbox][name="${fieldName}"]`);
        if (relatedChecks.length > 0) {
            relatedChecks.forEach((el) => {
                el.removeAttribute('required');
            });
        }
        return;
    }
    // if unchecked confirm if others are checked.
    const fieldSet = ev.target.closest('fieldset');
    if (fieldSet === null) {
        return;
    }
    // Get checked boxes (if any)
    const checkedBoxes = fieldSet.querySelectorAll('input[type=checkbox]:checked');
    if (checkedBoxes.length > 0) {
        // Others are checked so all good.
        return;
    }
    // unchecked
    fieldSet.querySelectorAll('input[type=checkbox]').forEach((el) => {
        el.setAttribute('required', '');
    });
};
/**
 * Add error on the form
 * @param {HTMLElement} input Element for input/select
 * @param {String} msg Error message
 */
bformView.markError = function(input, msg) {
    if (!input) {
        return;
    }
    const msg_div = document.createElement('div');
    msg_div.classList.add('error-message');
    msg_div.innerHTML = msg;
    const parent = input.closest('div.form-group');
    if (parent === null) {
        return;
    }
    input.setAttribute('aria-invalid', 'true');
    parent.classList.add('has-error');
    const label = parent.querySelector('label');
    if (label) {
        label.appendChild(msg_div);
    }
};
/**
 * Validate a single field for if it is set.
 * @param {Object} el DOMElement fields we are validating.
 */
bformView.validateField = function(el) {
    if (!el || el.disabled) {
        return;
    }
    if (el.tagName === 'SELECT') {
        const option = el.options[el.selectedIndex] || null;
        if (!option) {
            return;
        }
        const value = option.value;
        if (value === '' || value === '0') {
            this.markError(el, this.model.get('translation').ch_required_field);
        }
        return;
    }
    if (el.tagName === 'TEXTAREA') {
        if (el.value.trim() === '') {
            this.markError(el, this.model.get('translation').ch_required_field);
        }
        return;
    }
    if (el.tagName !== 'INPUT') {
        return;
    }
    // Ok just INPUT tags now.
    const inputType = el.type;
    // Radio and checkbox are handled as groups
    if (inputType === 'radio' || inputType === 'checkbox') {
        return;
    }
    // The rest... text mostly.
    if (el.value.trim() === '') {
        this.markError(el, this.model.get('translation').ch_required_field);
    }
};
/**
 * Validate checkbox and radio groups.
 * @param {HTMLElement} fieldset A fieldset element.
 */
bformView.validateFieldSet = function(fieldset) {
    const inputs = fieldset.querySelectorAll('input');
    let isValid = true;
    inputs.forEach((input) => {
        if (!input.checkValidity()) {
            isValid = false;
            input.setAttribute('aria-invalid', 'true');
        }
    });
    if (isValid) {
        return;
    }

    const msg_div = document.createElement('div');
    msg_div.classList.add('error-message');
    msg_div.innerHTML = this.model.get('translation').ch_required_field;

    fieldset.classList.add('has-error');
    const legend = fieldset.querySelector('legend');
    if (legend) {
        legend.appendChild(msg_div);
        return;
    }
};

bformView.validate = function() {
    this.el.querySelectorAll('label .error-message, legend .error-message').forEach((el) => {
        el.remove();
    });
    this.el.querySelectorAll('.form-group').forEach((el) => {
        el.classList.remove('has-error');
    });
    this.el.querySelectorAll('.form-control, input[type=radio], input[type=checkbox]').forEach((el) => {
        el.setAttribute('aria-invalid', false);
    });

    //check "required fields" for validity
    this.el.querySelectorAll('[required]').forEach((el) => {
        this.validateField(el);
    });
    //check required radio/checkbox groups
    this.el.querySelectorAll('fieldset[data-required]').forEach((el) => {
        this.validateFieldSet(el);
    });

    if (this.el.querySelectorAll('.has-error').length > 0) {
        this.el.querySelector('*[aria-invalid=true]')?.focus();
        return false;
    }
    return true;
};
/**
 * Create a dismissable alert for insertion into the DOM.
 * Uses bootstrap alert classes.
 * @param {String} message HTML Error message (html)
 * @param {String} colorClass Bootstrap based (danger, success, info, etc.)
 * @returns {HTMLElement}
 */
bformView.createDismissableAlert = function(message, colorClass) {
    const alert = document.createElement('div');
    alert.classList.add('alert', `alert-${colorClass}`, 'alert-dismissable');
    alert.setAttribute('role', 'alert');
    const button = document.createElement('button');
    button.classList.add('close');
    button.setAttribute('aria-label', 'close');
    button.innerHTML = '<span aria-hidden="true">&times;</span>';
    alert.appendChild(button);
    alert.insertAdjacentHTML('beforeend', message);
    button.addEventListener('click', (ev) => {
        ev.preventDefault();
        alert.remove();
    });
    return alert;
};

bformView.submitBooking = function(ev, action) {
    ev.preventDefault();
    if (this.submitted) {
        return;
    }
    if (!this.validate()) {
        return;
    }
    this.submitted = true;
    const form = ev.target;

    // in preview just fake success
    if (this.preview) {
        this.emitter.trigger('bot:booking:success', { action });
        this.emitter.trigger('changeView', 'chat');
        return;
    }

    fetch(
        form.action,
        {
            method: 'POST',
            mode: 'no-cors',
            credentials: 'omit',
            cache: 'no-cache',
            body: new FormData(form)
        }
    )
        .then((response) => {
            return response.json();
        })
        .then(({ id = 0, error = '' }) => {
            if (error || id === 0) {
                throw new Error(error);
            }
            this.emitter.trigger('bot:booking:success', { action });
            this.emitter.trigger('changeView', 'chat');
        })
        .catch((err) => {
            // Insert an alert above the submit button.
            const alert = this.createDismissableAlert(err.message, 'danger');
            const button = this.el.querySelector('button[type="submit"]');
            button.parentElement.insertAdjacentElement('beforebegin', alert);
            this.submitted = false;
        });

};
/**
 * Render the view
 * @return {bformView}
 */
bformView.render = function({
    action,
    html = ''
}) {
    // clear out existing content, events, etc.
    this.$el.empty();
    this.el.innerHTML = html;

    this.el.querySelector('form')?.addEventListener('submit', (ev) => {
        this.submitBooking(ev, action);
    });

    // Set email if possible
    const email = this.patronSession.get('client_contact');
    if (email) {
        const emailInput = this.el.querySelector('form input[name="email"]');
        if (emailInput) {
            emailInput.value = email;
        }
    }

    // Cancel button
    this.el.querySelector('.btn-cancel').addEventListener('click', () => {
        this.emitter.trigger('bot:booking:cancel', { action });
        this.emitter.trigger('changeView', 'chat');
    });

    this.el.querySelectorAll('input[type=checkbox][required]').forEach((el) => {
        el.addEventListener('change', this.handleRequiredCheckBoxes.bind(this));
    });

    return this;
};

export default bformView;
